'use client';

import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { Page, useTranslation } from '@weareconceptstudio/core';
import { config } from '@/helpers';

//* Components
const Footer = dynamic(() => import('../Footer'));
import ScrollWrapper from '../ScrollWrapper';
import { usePathname } from 'next/navigation';
import { useDeviceDetection } from '../ScrollWrapper/useDeviceDetection';

const HighOrderComponent = ({ children }) => {
	const { isClient, hasTouchScreen, isDesktop } = useDeviceDetection();
	const pathname = usePathname();
	const { selectedLang } = useTranslation();

	//* Memoized pathname cleaning
	const cleanedPathname = useMemo(() => {
		if (!selectedLang) return pathname;
		return pathname.replaceAll(`/${selectedLang}`, '');
	}, [pathname, selectedLang]);

	//* Memoized incentives page check
	const isIncentivesPage = useMemo(() => {
		return cleanedPathname.includes('/business-environment/incentives/');
	}, [cleanedPathname]);

	//* Memoized route configuration check
	const isWithoutMainDistance = useMemo(() => {
		return Object.values(config.routes).some((route) => route.isWithoutMainDistance && cleanedPathname === route.path) || isIncentivesPage;
	}, [cleanedPathname, isIncentivesPage]);

	//* Memoized smooth scroll eligibility
	const isSmooth = useMemo(() => {
		return isClient && !hasTouchScreen && isDesktop;
	}, [isClient, hasTouchScreen, isDesktop]);

	//* Memoized className calculation
	const containerClassName = useMemo(() => {
		const baseClasses = ['main-container'];

		if (isClient) {
			baseClasses.push(isSmooth ? 'desktop-version' : 'mobile-version');
		}

		if (isWithoutMainDistance) {
			baseClasses.push('no-padding');
		}

		return baseClasses.join(' ');
	}, [isClient, isSmooth, isWithoutMainDistance]);

	return (
		<ScrollWrapper isEnabled={isSmooth}>
			<Page className={containerClassName}>{children}</Page>
			<Footer />
		</ScrollWrapper>
	);
};

HighOrderComponent.propTypes = {
	children: PropTypes.node.isRequired,
};

export default HighOrderComponent;
